import {AudienceType, SurveySlug} from 'client/common/types';

import {EditMode, SurveyState} from 'client/common/store/reducers/survey/types';

import {useAudienceRequest} from 'client/common/hooks/audience';
import {useBoxType} from 'client/common/hooks/box';
import {useSurvey} from 'client/common/hooks/survey';
import {useIsCreativeAssessor, useIsSuperuser} from 'client/common/hooks/user';

import {useSurveySlug} from 'client/common/contexts/slug';
import {usePagesContext} from 'client/common/contexts/pages';
import {usePrice} from './payment';

// eslint-disable-next-line max-statements
function useEditMode(slug: SurveySlug): EditMode {
    const survey = useSurvey(slug);
    const isSuperuser = useIsSuperuser();
    const {isScreening} = usePagesContext();

    if (survey.status !== 'success') {
        return EditMode.ReadOnly;
    }

    if (isScreening && !isSuperuser) {
        return EditMode.ReadOnly;
    }

    if (survey.data.state === SurveyState.Draft) {
        return EditMode.Everything;
    }

    if (survey.data.state === SurveyState.Moderation) {
        return isSuperuser ? EditMode.Everything : EditMode.Launched;
    }

    if ([SurveyState.Active, SurveyState.Paused].includes(survey.data.state)) {
        return isSuperuser ? EditMode.Everything : EditMode.Activated;
    }

    return EditMode.Completed;
}

export function useCanEditRevision(): boolean {
    const slug = useSurveySlug();
    const boxType = useBoxType(slug);

    const isCreativeAssessor = useIsCreativeAssessor();

    const editMode = useEditMode(slug);
    const audienceRequest = useAudienceRequest(slug);

    if (audienceRequest.data?.type === AudienceType.None) {
        return editMode < EditMode.Completed;
    }

    if (isCreativeAssessor && boxType && ['creative', 'bordomatik'].includes(boxType)) {
        return editMode <= EditMode.Launched;
    }

    return editMode < EditMode.Launched;
}

export function useCanEditAudience(): boolean {
    const slug = useSurveySlug();
    const boxType = useBoxType(slug);
    const editMode = useEditMode(slug);
    const surveyPrice = usePrice(slug);
    const isSuperuser = useIsSuperuser();

    // если цену опроса нельзя менять (ручная цена с totalCost)
    // даем  править аудиторию только админам
    if (!surveyPrice.data?.overwritable) {
        return isSuperuser;
    }

    if (boxType === 'creative') {
        return editMode <= EditMode.Launched;
    }

    return editMode < EditMode.Launched;
}

export function useCanEditName(): boolean {
    const slug = useSurveySlug();
    const editMode = useEditMode(slug);

    return editMode <= EditMode.Completed;
}

export function useCanEditSurveySettings(): boolean {
    const slug = useSurveySlug();
    const editMode = useEditMode(slug);

    return editMode < EditMode.Completed;
}

export function useCanEditAccess(): boolean {
    const slug = useSurveySlug();
    const editMode = useEditMode(slug);

    return editMode <= EditMode.Completed;
}

export function useIsClosedOrDraft(slug: SurveySlug): boolean {
    const survey = useSurvey(slug);

    return survey.data?.state === SurveyState.Closed || survey.data?.state === SurveyState.Draft;
}

export function useIsClosed(slug: SurveySlug): boolean {
    const survey = useSurvey(slug);

    return survey.data?.state === SurveyState.Closed;
}
